import TitleSection from "../../text/TitleSection/TitleSection";
import * as Tabs from "@radix-ui/react-tabs";
import * as React from "react";

interface SectionItem {
  id: string;
  title: string;
}

export default function SectionTabs({
  title,
  description,
  children,
  items,
  disableMinHeight = false,
}: {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  children?: React.ReactNode;
  items?: SectionItem[];
  disableMinHeight?: boolean;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Count children to determine section items
  const childrenArray = React.Children.toArray(children);

  // Generate default items if not provided
  const sectionItems =
    items ||
    childrenArray.map((child, index) => {
      // Extract meta from nested FeatureShowcase component
      let title = `Item ${index + 1}`;

      if (React.isValidElement(child)) {
        // Check if child has a FeatureShowcase as a descendant
        const childProps = child.props as any;
        if (childProps.children && React.isValidElement(childProps.children)) {
          const nestedChild = childProps.children;
          if (nestedChild.props && nestedChild.props.meta) {
            title = nestedChild.props.meta;
          }
        }
      }

      return {
        id: `item-${index + 1}`,
        title,
      };
    });

  const [activeTab, setActiveTab] = React.useState(sectionItems[0]?.id || "1");

  // Update active tab based on scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const triggerPoint = scrollTop + viewportHeight / 2;

      // Find which section is currently in view
      let currentSection = 0;
      for (let i = 0; i < sectionRefs.current.length; i++) {
        const section = sectionRefs.current[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          const sectionTop = scrollTop + rect.top;
          const sectionBottom = sectionTop + rect.height;

          if (triggerPoint >= sectionTop && triggerPoint < sectionBottom) {
            currentSection = i;
            break;
          } else if (triggerPoint >= sectionBottom) {
            currentSection = i;
          }
        }
      }

      setActiveTab(sectionItems[currentSection]?.id || "1");
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionItems]);

  // Handle tab click to scroll to specific section
  const handleTabChange = (value: string) => {
    const sectionIndex = sectionItems.findIndex(item => item.id === value);
    if (sectionIndex !== -1 && sectionRefs.current[sectionIndex]) {
      const section = sectionRefs.current[sectionIndex];
      if (section && headerRef.current) {
        // Get the section's absolute position on the page
        const sectionRect = section.getBoundingClientRect();
        const sectionTop = window.scrollY + sectionRect.top;
        // Get the sticky header height
        const headerHeight = headerRef.current.offsetHeight || 0;
        // Scroll so the section is 5px below the sticky tabs
        const scrollPosition = sectionTop - headerHeight - 5;

        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div ref={containerRef} className="tw-bg-gray_scale-900 tw-relative">
      {/* Wrapper that ends before the last section */}
      <TitleSection headingElement="h2" title={title} description={description} />
      <div className="tw-relative">
        {/* Sticky header with tabs */}
        <div ref={headerRef} className="tw-bg-black md:tw-sticky md:tw-top-0 md:tw-z-10">
          <div className="tw-mx-auto tw-w-full tw-px-6 tw-pb-2 lg:tw-px-8 lg:tw-pt-10">
            <Tabs.Root value={activeTab} onValueChange={handleTabChange} className="tw-w-full">
              <Tabs.List className="tw-flex tw-flex-wrap tw-justify-center tw-gap-1.5 tw-py-2 sm:tw-gap-2 sm:tw-py-4">
                {sectionItems.map(item => (
                  <Tabs.Trigger
                    key={item.id}
                    value={item.id}
                    className="tw-rounded-md tw-border tw-border-solid tw-border-[var(--tab-border-color)] tw-px-2 tw-py-1 tw-text-sm tw-font-medium tw-text-[var(--foreground-color)] tw-transition-[background,color,padding,margin,transform,opacity] hover:tw-text-[var(--foreground-color-muted)] data-[state=active]:tw-border-brand_scale-500 data-[state=active]:tw-bg-primary-gradient data-[state=active]:tw-text-white sm:tw-px-3 sm:tw-py-1"
                  >
                    {item.title.split(" section")[0]}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            </Tabs.Root>
          </div>
        </div>

        {/* All sections except the last one */}
        <div className="tw-mx-auto tw-max-w-[2000px]">
          {React.Children.map(children, (child, index) => {
            if (index === childrenArray.length - 1) return null; // Skip last section
            return (
              <div
                key={sectionItems[index]?.id || index}
                ref={el => (sectionRefs.current[index] = el)}
                className={disableMinHeight ? "tw-py-4 md:tw-py-8" : "tw-min-h-[calc(100vh-200px)] tw-py-4 md:tw-py-8"}
              >
                {child}
              </div>
            );
          })}
        </div>
      </div>

      {/* Last section - outside the sticky container */}
      <div className="tw-mx-auto tw-max-w-[2000px]">
        {childrenArray.length > 0 && (
          <div
            key={sectionItems[childrenArray.length - 1]?.id || childrenArray.length - 1}
            ref={el => (sectionRefs.current[childrenArray.length - 1] = el)}
            className={disableMinHeight ? "tw-py-4 md:tw-py-8" : "tw-min-h-[calc(100vh-200px)] tw-py-4 md:tw-py-8"}
          >
            {childrenArray[childrenArray.length - 1]}
          </div>
        )}
      </div>
    </div>
  );
}
